import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography
} from "@mui/material"
import { useState, useEffect } from "react"
import { toast } from "react-toastify"
import { Dialog } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import {
  useCreatePrinterConfigMutation,
  useUpdatePrinterConfigMutation
} from "src/graphql/quark/quark.type"
import type { LabelLayoutSettingsValues } from "./types"
import {
  labelSettingsToCreateDto,
  labelSettingsToUpdateDto
} from "./printer-config-mappers"

export type PrinterConfigModalMode = "create" | "edit"

interface PrinterConfigModalProps {
  open: boolean
  onClose: () => void
  mode: PrinterConfigModalMode
  /** Current label layout settings (used for create/update payload). */
  config: LabelLayoutSettingsValues
  /** For edit mode: existing config id. */
  editId?: string
  /** For edit mode: existing name. */
  editName?: string
  /** For edit mode: existing isDefault. */
  editIsDefault?: boolean
  onSuccess: () => void
}

export default function PrinterConfigModal({
  open,
  onClose,
  mode,
  config,
  editId = "",
  editName = "",
  editIsDefault = false,
  onSuccess
}: PrinterConfigModalProps) {
  const [name, setName] = useState(editName)
  const [isDefault, setIsDefault] = useState(editIsDefault)

  useEffect(() => {
    if (open) {
      setName(mode === "edit" ? editName : "")
      setIsDefault(mode === "edit" ? editIsDefault : false)
    }
  }, [open, mode, editName, editIsDefault])

  const [createMutation, { loading: createLoading }] = useCreatePrinterConfigMutation({
    onCompleted: () => {
      toast.success("Printer config saved.")
      onClose()
      onSuccess()
    },
    onError: (e) => {
      toast.error(e.message || "Failed to save printer config.")
    }
  })

  const [updateMutation, { loading: updateLoading }] = useUpdatePrinterConfigMutation({
    onCompleted: () => {
      toast.success("Printer config updated.")
      onClose()
      onSuccess()
    },
    onError: (e) => {
      toast.error(e.message || "Failed to update printer config.")
    }
  })

  const loading = createLoading || updateLoading
  const isEdit = mode === "edit"

  const handleSubmit = () => {
    const trimmed = name?.trim()
    if (!trimmed) {
      toast.error("Please enter a name.")
      return
    }
    if (isEdit && !editId) {
      toast.error("Missing config id.")
      return
    }
    if (isEdit) {
      updateMutation({
        variables: {
          updatePrinterConfigDto: labelSettingsToUpdateDto(editId, config, trimmed, isDefault)
        }
      })
    } else {
      createMutation({
        variables: {
          createPrinterConfigDto: labelSettingsToCreateDto(config, trimmed, isDefault)
        }
      })
    }
  }

  const handleCancel = () => {
    if (!loading) onClose()
  }

  return (
    <Dialog
      open={open}
      toggleDialog={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="h2">
            {isEdit ? "Edit printer config" : "Save as printer config"}
          </Typography>
          <IconButton aria-label="close" onClick={handleCancel} sx={{ color: (t) => t.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <OutlinedInput
            label="Config name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Thermal 50x25"
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                color="primary"
              />
            }
            label="Use as default for this location"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={handleCancel} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !name?.trim()}
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? (isEdit ? "Updating..." : "Saving...") : isEdit ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
